'use client'

import { useForm } from 'react-hook-form'

import { cn } from '@/_lib/utils'
import { useCreateOrder } from '@/app/[locale]/order/_services/useCreateOrder'
import { useGetInsuranceCompanies } from '@/app/[locale]/order/_services/useGetInsuranceCompanies'
import { useGetTimeSlotDetail } from '@/app/[locale]/order/_services/useGetTimeSlotDetail'
import { useGetYearProgram } from '@/app/[locale]/order/_services/useGetYearProgram'
import { validateBirthNumber } from '@/app/[locale]/order/_utils/validate-birth-number'
import { AOrderCartSidebar } from '@cart/_components/a-cart-sidebar'
import { useCartPrices } from '@cart/_hooks/use-cart-prices'

import { CartProduct, CartProductType, useOrderFlow } from '../../../_context/order-flow-context'

import { APatientInfoAddressSection } from './_components/a-patient-info-address-section'
import { APatientInfoAnnualProgramHighlight } from './_components/a-patient-info-annual-program-highlight'
import { APatientInfoCartPriceProbe } from './_components/a-patient-info-cart-price-probe'
import { APatientInfoFloatingSubmit } from './_components/a-patient-info-floating-submit'
import { APatientInfoInsuranceDescriptionCallout } from './_components/a-patient-info-insurance-description-callout'
import { APatientInfoLegalGuardianSection } from './_components/a-patient-info-legal-guardian-section'
import { APatientInfoMessageConsentsSection } from './_components/a-patient-info-message-consents-section'
import { APatientInfoPersonalDetailsSection } from './_components/a-patient-info-personal-details-section'
import { useAnnualProgramDiscount } from './_hooks/use-annual-program-discount'
import { useBeginCheckoutAnalytics } from './_hooks/use-begin-checkout-analytics'
import { useMobileSubmitDock } from './_hooks/use-mobile-submit-dock'
import { useOrderPatientSubmitCta } from './_hooks/use-order-patient-submit-cta'
import { usePatientInfoSubmit } from './_hooks/use-patient-info-submit'
import { usePatientInsurance } from './_hooks/use-patient-insurance'
import { useTemplateCartPricingSync } from './_hooks/use-template-cart-pricing-sync'
import { PATIENT_INFO_FORM_ID } from './_utils/patient-form.constants'

import type { PatientFormValues } from './_types/patient-form.types'

/**
 * Order step 3: patient info form.
 *
 * Handles all use-cases carried over from the legacy order form:
 * - Adult vs. child patient (derived from birth number — age < 18 → legal guardian section)
 * - Insurance selector (visibility depends on product type via `canShowInsurance`)
 * - EU insurance extra fields (euInsuranceNumber, contractualInsurance, countryId)
 * - Annual-program discount check (TEMPLATE products only — debounced API call)
 * - Full validation parity with the original `a-order-form.tsx`
 *
 * Layout: sidebar (1/3, desktop only, hidden on mobile) + form (2/3 desktop / full mobile).
 */
export function AStepPatientInfo() {
	const { cartProducts, ambulancePriceListTableType, templateCartPricing } = useOrderFlow()
	const insuranceCompanyPending = templateCartPricing?.showFromPrefix ?? false
	const { productPrices, reportPrice, pricesLoaded, totalAll } = useCartPrices(cartProducts)
	const createOrderMutation = useCreateOrder()
	const { submitAnchorRef, showFloatingSubmit, isLg } = useMobileSubmitDock()

	const primaryProduct = cartProducts[0] as CartProduct | undefined
	const primaryType = primaryProduct?.type

	const annualProgramId =
		primaryProduct?.type === CartProductType.ANNUAL_PROGRAM ||
		primaryProduct?.type === CartProductType.ANNUAL_PROGRAM_FAMILY
			? primaryProduct.data.programId
			: 0
	const { data: yearProgramResponse } = useGetYearProgram(annualProgramId)

	const timeslotId =
		primaryProduct?.type === CartProductType.TEMPLATE ? primaryProduct.data.timeslotId : null
	const { data: timeslotDetail } = useGetTimeSlotDetail(timeslotId)

	const { data: allInsuranceData } = useGetInsuranceCompanies()

	const {
		register,
		handleSubmit,
		watch,
		control,
		formState: { errors }
	} = useForm<PatientFormValues>({
		defaultValues: {
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
			birthNumber: '',
			address: '',
			city: '',
			zipCode: '',
			message: '',
			privacyPolicy: false,
			marketing: false,
			terms: false,
			parent: { firstName: '', lastName: '', email: '', phone: '' }
		}
	})

	const firstName = watch('firstName')
	const lastName = watch('lastName')
	const birthNumber = watch('birthNumber')
	const watchedInsurance = watch('insurance')

	const birthNumberValidation = birthNumber ? validateBirthNumber(birthNumber) : null
	const isChildOrder = !!(
		birthNumberValidation &&
		birthNumberValidation.isValid &&
		birthNumberValidation.age < 18
	)

	const { showInsurance, insuranceSelectOptions, isEUInsurance } = usePatientInsurance({
		primaryType,
		timeslotDetail,
		allInsuranceData,
		watchedInsurance
	})

	const templateId = timeslotDetail?.data?.template?.id

	const { annualProgramDiscount, isCheckingDiscount } = useAnnualProgramDiscount({
		primaryType,
		firstName,
		lastName,
		birthNumber,
		errors,
		birthNumberValidation,
		templateId
	})

	useTemplateCartPricingSync({
		primaryProduct,
		primaryType,
		timeslotDetail,
		showInsurance,
		watchedInsurance,
		annualProgramDiscount
	})

	const { submitButtonLabel, submitDisabled, priceResult } = useOrderPatientSubmitCta({
		createOrderIsPending: createOrderMutation.isPending,
		isCheckingDiscount,
		pricesLoaded,
		cartProductsLength: cartProducts.length,
		totalAll,
		primaryType,
		timeslotDetail,
		showInsurance,
		watchedInsurance,
		annualProgramDiscount,
		ambulancePriceListTableType,
		insuranceCompanyPending
	})

	const onSubmit = usePatientInfoSubmit({
		primaryProduct,
		isChildOrder,
		isEUInsurance,
		cartProducts,
		createOrderMutation
	})

	useBeginCheckoutAnalytics({
		cartProducts,
		productPrices,
		pricesLoaded,
		totalAll,
		primaryType,
		annualProgramId,
		yearProgramResponse,
		timeslotDetail
	})

	if (!primaryProduct) return null

	return (
		<div className='flex flex-col gap-8 lg:flex-row'>
			<APatientInfoCartPriceProbe cartProducts={cartProducts} reportPrice={reportPrice} />

			<AOrderCartSidebar placement='mobileAboveForm' />

			<div className='hidden w-full shrink-0 lg:block lg:w-1/3'>
				<AOrderCartSidebar placement='desktop' />
			</div>

			<div className='w-full lg:w-2/3'>
				<form
					id={PATIENT_INFO_FORM_ID}
					className={cn(showFloatingSubmit && 'pb-24')}
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<APatientInfoPersonalDetailsSection
						register={register}
						control={control}
						errors={errors}
						isChildOrder={isChildOrder}
						showInsurance={showInsurance}
						isEUInsurance={isEUInsurance}
						insuranceSelectOptions={insuranceSelectOptions}
					/>

					<APatientInfoAddressSection register={register} errors={errors} />

					<APatientInfoLegalGuardianSection
						register={register}
						control={control}
						errors={errors}
						isChildOrder={isChildOrder}
					/>

					{priceResult?.annualProgramHighlight && (
						<APatientInfoAnnualProgramHighlight
							highlight={priceResult.annualProgramHighlight}
						/>
					)}

					<APatientInfoMessageConsentsSection register={register} errors={errors} />

					{priceResult?.description && (
						<APatientInfoInsuranceDescriptionCallout
							description={priceResult.description}
						/>
					)}

					<div
						ref={submitAnchorRef}
						className='flex w-full flex-col items-center md:items-start gap-3'
					>
						<button
							id='order-submit-button'
							type='submit'
							disabled={submitDisabled}
							className={cn('btn_basic cyclamen ')}
						>
							<span>{submitButtonLabel}</span>
						</button>
					</div>
				</form>

				<APatientInfoFloatingSubmit
					isLg={isLg}
					showFloatingSubmit={showFloatingSubmit}
					submitDisabled={submitDisabled}
					submitButtonLabel={submitButtonLabel}
				/>
			</div>
		</div>
	)
}
